import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Phone, Mail, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TechnicianRoute } from '../types';

interface TechnicianCardProps {
  route: TechnicianRoute;
  isSelected: boolean;
  onSelect: (route: TechnicianRoute) => void;
  onPrintRoute?: (route: TechnicianRoute) => void;
  // Deprecated props - confirm functionality removed (routes auto-transition)
  /** @deprecated Confirm functionality removed */
  onConfirmRoute?: (route: TechnicianRoute) => void;
  /** @deprecated Confirm functionality removed */
  isConfirming?: boolean;
  /** @deprecated Confirm functionality removed */
  isConfirmed?: boolean;
}

const TechnicianCard: React.FC<TechnicianCardProps> = ({
  route,
  isSelected,
  onSelect,
  onPrintRoute
}) => {
  const technician = route.technician;

  return (
    <Card
      className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
        isSelected ? 'ring-2 ring-blue-500 bg-blue-500/10 dark:bg-blue-500/20' : 'hover:bg-muted'
      }`}
      onClick={() => onSelect(route)}
    >
      <CardContent className='p-4'>
        {/* Technician Header */}
        <div className='flex items-center gap-3 mb-3'>
          <div
            className='w-3 h-3 rounded-full flex-shrink-0'
            style={{ backgroundColor: route.route_color }}
          />
          <div className='flex-1 min-w-0'>
            <div className='flex items-center justify-between'>
              <h3 className='font-semibold text-foreground truncate'>{technician.full_name}</h3>
              <Badge
                variant='secondary'
                className='text-xs bg-blue-500/20 text-blue-400 border-blue-500/30 dark:bg-blue-500/30 dark:text-blue-300 dark:border-blue-400/30'
              >
                Assigned
              </Badge>
            </div>
            <div className='flex items-center gap-2 mt-1'>
              <Badge variant='secondary' className='text-xs'>
                Skill Level {technician.skill_level}
              </Badge>
              <Badge variant='outline' className='text-xs'>
                {route.stops.length} stops
              </Badge>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className='space-y-1 mb-3'>
          {technician.phone_number && (
            <div className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Phone className='w-3 h-3 flex-shrink-0' />
              <span className='truncate'>{technician.phone_number}</span>
            </div>
          )}
          {technician.email && (
            <div className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Mail className='w-3 h-3 flex-shrink-0' />
              <span className='truncate'>{technician.email}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {onPrintRoute && (
          <div className='mt-3 pt-3 border-t border-border'>
            <Button
              variant='outline'
              size='sm'
              onClick={e => {
                e.stopPropagation();
                onPrintRoute(route);
              }}
              className='text-xs w-full'
            >
              <Download className='h-3 w-3 mr-1' />
              Print Route
            </Button>
          </div>
        )}

        {/* Selection Indicator */}
        {isSelected && (
          <div className='mt-2'>
            <div className='flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400'>
              <div className='w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full' />
              <span className='font-medium'>Selected Route</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TechnicianCard;
